import { apiFetch, type ApiFetchOptions } from '../lib/api'

export interface DocRow {
  id: string
  filename: string
  display_title?: string | null
  document_type: string | null
  fund_ids: string[]
  fund_names: string[]
  upload_date: string
  status: string
  page_count: number | null
  unlinked_payment_observations?: number
  failure_reason: string | null
  source_language: string | null
}

/** The existing list is paginated. Search and counts must include every page. */
export async function fetchDocuments(
  options: ApiFetchOptions = {},
  status?: string
): Promise<DocRow[]> {
  const result = new Map<string, DocRow>()
  for (let page = 1; ; page++) {
    const rows = await apiFetch<DocRow[]>(
      `/api/documents?sort=upload_date&order=desc&page_size=500&page=${page}${status ? `&status=${encodeURIComponent(status)}` : ''}`,
      options
    )
    const before = result.size
    for (const row of rows) result.set(row.id, row)
    if (rows.length < 500) return [...result.values()]
    if (result.size === before)
      throw new Error('The document list changed while loading. Please retry.')
  }
}

export interface OrphanRow {
  document_id: string
  filename: string
  display_title?: string | null
  parked_at: string
  reason: string
  resolved_entity_count: number
}
export const NEEDS_ATTENTION = new Set(['failed', 'unprocessable', 'needs_reclassify'])
export const PROCESSING = new Set(['uploaded', 'processing', 'split'])

export async function fetchOrphans(options: ApiFetchOptions = {}): Promise<OrphanRow[]> {
  const rows = new Map<string, OrphanRow>()
  for (let page = 1; ; page++) {
    const result = await apiFetch<{ orphans: OrphanRow[]; total?: number }>(
      `/api/orphans?page_size=200&page=${page}`,
      options
    )
    const before = rows.size
    for (const row of result.orphans) rows.set(row.document_id, row)
    if (result.orphans.length < 200 || (result.total != null && rows.size >= result.total))
      return [...rows.values()]
    if (rows.size === before) throw new Error('The unmatched document list changed. Please retry.')
  }
}

export function documentNeedsAttention(doc: DocRow, parkedIds: Set<string>): boolean {
  return (
    NEEDS_ATTENTION.has(doc.status) ||
    parkedIds.has(doc.id) ||
    (doc.unlinked_payment_observations ?? 0) > 0
  )
}

export function documentReceivedSince(doc: DocRow, since: string): boolean {
  return Date.parse(doc.upload_date) >= Date.parse(since)
}
